/* eslint-disable react/jsx-max-depth */
import type { ThemeOptions } from '@mui/material';

import { breakpoints } from './breakpoints';
import Palette from './palette';
import { shadows } from './shadows';
import { FontWeight, typography } from './typography';

const baseThemeOptions: ThemeOptions = {
  palette: Palette.light,
  shadows,
  typography,
  breakpoints,
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        option: ({ theme: { palette } }) => ({
          fontSize: 13,
          padding: 4,
          paddingLeft: 8,
          paddingRight: 8,
          borderBottom: `1px solid ${palette.divider}`,
          lineHeight: '150%',
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '&.MuiTextField-root': {
            padding: 0,
          },
          '& > .MuiAutocomplete-inputRoot': {
            padding: 5,
          },
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        standard: ({ theme, ownerState }) => ({
          border: `1px solid ${
            theme.palette[ownerState.severity ?? 'success'].main
          }`,
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
        root: ({
          theme: {
            palette: { text },
          },
        }) => ({
          color: text.primary,
          fontSize: '0.875rem',
          lineHeight: '1.33rem',
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontFamily: 'inherit',
          fontSize: 'inherit',
          color: 'inherit',
          height: '100%',
        },
        root: {
          fontFamily: 'inherit',
          fontSize: 'inherit',
          color: 'inherit',
          height: '100%',
        },
        adornedStart: {
          padding: 0,
        },
        adornedEnd: {
          paddingRight: '8px !important',
        },
        inputAdornedEnd: {
          paddingRight: '0px !important',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
        root: ({
          theme: {
            palette: { text },
          },
        }) => ({
          color: text.primary,
          fontSize: '0.875rem',
          lineHeight: '1.33rem',
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          cursor: 'pointer',
          color: theme.palette.info.main,
          fontSize: '14px',
          fontWeight: 400,
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          border: 'none',
        },
        root: ({ theme }) => ({
          boxSizing: 'border-box',
          width: '100%',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '7px',
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          fontSize: '16px',
          minHeight: 47,
          minWidth: 75,
          '&::placeholder': {
            color: theme.palette.grey.A700,
          },
          '&.Mui-focused': {
            boxShadow: theme.shadows[4],
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
          '&.Mui-error': {
            border: `1px solid ${theme.palette.error.main}`,
          },
          '&.Mui-warning': {
            border: `1px solid ${theme.palette.warning.main}`,
          },
          '&.Mui-success': {
            border: `1px solid ${theme.palette.success.main}`,
          },
          [`${theme.breakpoints.down('sm')}`]: {
            backgroundColor: theme.palette.background.paper,
          },
        }),
        input: {
          paddingTop: '13.5px',
          paddingBottom: '13.5px',
          paddingLeft: '8px',
          paddingRight: '8px',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          zIndex: 3,
        },
        regular: {
          minHeight: 'auto',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.text.primary,
        }),
      },
      defaultProps: {
        arrow: true,
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          background: 'transparent',
          fontSize: '14px',
          width: '100%',
          appearance: 'none',
          border: '0',
          padding: '4.5px 14px',
          paddingLeft: '10px',
        },
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontSize: '14px',
          overflow: 'hidden',
          height: '45px',
          width: '100%',
          borderRadius: '7px',
          backgroundColor: theme.palette.background.paper,
          border: 0,
          cursor: 'pointer',
          position: 'relative',
          minWidth: '100px',
          boxSizing: 'border-box',
          lineHeight: '38px',
          boxShadow: theme.shadows[1],
          '&:focus-within': {
            backgroundColor: theme.palette.background.default,
          },
          '&:hover': {
            backgroundColor: theme.palette.background.default,
          },
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: theme.shadows[1],
          fontWeight: 'normal',
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
          color: 'textPrimary',
        },
        subheaderTypographyProps: {
          variant: 'subtitle2',
          color: 'textSecondary',
        },
      },
      styleOverrides: {
        action: {
          marginRight: 0,
          alignSelf: 'center',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '&:last-child': {
            padding: 16,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          borderRadius: '16px',
          fontWeight: 'lighter',
          textAlign: 'left',
          border: 'none',
          transition: 'boxShadow 0.1s',
          overflow: 'hidden',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          position: 'fixed',
          top: 0,
          left: 0,
        },
        paper: {
          minWidth: 100,
          fontWeight: 'unset',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: '29px',
          transition: 'all 0.1s linear',
          minHeight: '32px',
          textTransform: 'uppercase',
          // fontSize: '14px',
          fontWeight: 500,
          textAlign: 'center',
          opacity: '1.0',
          '&.Mui-disabled': {
            opacity: '0.4',
            boxShadow: 'none',
          },
          lineHeight: 1.25,
          minWidth: '90px',
        },
        contained: ({ theme }) => ({
          boxShadow: theme.shadows[1],
          border: 'none',
        }),
        outlined: ({ theme }) => ({
          boxShadow: theme.shadows[1],
        }),
      },
    },
    MuiSkeleton: {
      defaultProps: {
        variant: 'rounded',
        animation: 'wave',
      },
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
        circular: {
          borderRadius: '50%',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        deleteIcon: {
          fontSize: 18,
        },
        label: {
          fontWeight: FontWeight.Regular,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: '56px',
          width: '100%',
          transition: 'background-color 0.05s linear',
          '&.Mui-selected': {
            backgroundColor: theme.palette.divider,
          },
          '&:hover': {
            backgroundColor: theme.palette.divider,
          },
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          minHeight: '56px',
        },
      },
    },
    MuiListItemText: {
      defaultProps: {
        primaryTypographyProps: {
          variant: 'body1',
        },
        secondaryTypographyProps: {
          variant: 'caption',
        },
      },
    },
    MuiStack: {
      defaultProps: {
        spacing: 1,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          alignItems: ownerState.direction === 'row' ? 'center' : undefined,
        }),
      },
    },
  },
};

export { baseThemeOptions };
